import type { CSSProperties, ReactNode } from 'react'
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import type { ShareFrame } from '~/api/types'
import { bubbleFor } from '~/components/bubbles'

import './share.css'
import s from './Share.module.css'

/*
 * The frame every card is drawn in, and the three things every format shares: the safe
 * zones, the wordmark, and the one signal the renderer waits for.
 *
 * Instagram crops. A 4:5 post is shown in the profile grid as a 3:4 thumbnail at full height,
 * which takes 34 px off each side; a story carries the platform's own chrome across the top
 * and bottom. The insets below are inline padding on the root rather than a stylesheet rule
 * so a test can read them off the element, and so a format cannot quietly opt out.
 */

/** The two frames: a 4:5 post and a 9:16 story. */
export const POST = { width: 1080, height: 1350 } as const
export const STORY = { width: 1080, height: 1920 } as const

/** A post keeps everything this far from both vertical edges — twice what the grid trims. */
export const POST_INSET = 72
/** And this far from the top and the bottom, which nothing crops; it is for the eye. */
export const POST_BAND = 80
/** A story's content box begins below the progress bar and handle … */
export const STORY_TOP = 250
/** … and ends above the reply box. */
export const STORY_BOTTOM = 340

/** The narrowest the wordmark is ever drawn: a fifth of the frame, legible on a phone. */
export const WORDMARK = 240

/** A link line longer than this is the host alone; a line of type is not a URL bar. */
export const LINK_LENGTH = 48

export type Format = 'A' | 'B' | 'C' | 'D' | 'E' | 'J'

/** The size each frame is — the only place the two are chosen between. */
export const FRAMES: Record<ShareFrame, typeof POST | typeof STORY> = { post: POST, story: STORY }

/**
 * Which formats are drawn as a story when nobody says otherwise. The rest default to posts.
 *
 * A *default*, not the whole answer, since the week and the gig card take either: the server
 * decides which frames a card has (`SHARE_FRAMES`) and puts the chosen one on the payload, and
 * `catalogue.ts` is the browser's half of that list. This is what a format falls back to for a
 * caller that has no payload — a test rendering a card, or the studio before one has arrived.
 */
export const STORY_FORMATS: readonly Format[] = ['E', 'J']

/** Which frame a format is drawn in with nothing chosen. */
export function defaultFrame(format: Format): ShareFrame {
  return STORY_FORMATS.includes(format) ? 'story' : 'post'
}

/** The size a format is drawn at, in `frame` if one is named and in its default if not. */
export function frameFor(format: Format, frame?: ShareFrame): typeof POST | typeof STORY {
  return FRAMES[frame ?? defaultFrame(format)]
}

interface Gate {
  start: (id: string) => void
  end: (id: string) => void
}

const Loading = createContext<Gate>({ start: () => {}, end: () => {} })

/**
 * Whether everything the card is made of has arrived: the two faces, and every picture that
 * registered itself. The same condition `data-ready` carries, and therefore the same moment
 * the renderer captures at.
 *
 * Exported because one card has to measure itself before it is drawn on. Until the wordmark
 * has loaded the foot has almost no height, so the line between them is taller than it will
 * be, and a card measured then would think it had room it is about to lose. A context and
 * not a prop: a consumer re-renders when this flips, where a child element the parent did
 * not rebuild would not — and the card's own component is `Frame`'s parent, so it cannot
 * read this itself.
 */
export const Drawn = createContext(false)

/**
 * Which faces the card is set in, asked for by name so the wait below is for *these* and
 * not for whatever happened to be in flight when the promise was taken.
 *
 * `document.fonts.ready` alone is not enough: it resolves once nothing is loading, and a
 * face nothing has laid out yet is not loading. Asking `load` for each starts the fetch and
 * returns when it lands; `ready` afterwards covers a subset a glyph pulled in meanwhile.
 * A test environment with no `FontFaceSet` has nothing to wait for.
 */
const FACES = ['400 1em Jost', '500 1em Jost', '400 1em "IBM Plex Mono"', '500 1em "IBM Plex Mono"']

async function fontsSettled(): Promise<void> {
  const fonts = document.fonts
  if (!fonts) return
  if (typeof fonts.load === 'function') await Promise.all(FACES.map(face => fonts.load(face)))
  await fonts.ready
}

interface FrameProps {
  format: Format
  /** The frame this one is being drawn in. The format's own default when it is not given. */
  frame?: ShareFrame
  className: string
  /**
   * What a card works out from its own payload, on the root because that is where it has to
   * be inherited from — the week's type factor, which it settles by measuring the card it
   * drew. The same mechanism as `Line`'s `style`, one level up: a custom property a rule
   * below reads, never a rule of its own. Merged *under* the frame's own size and padding,
   * so a card cannot reach past the safe zone by passing one of those names.
   */
  style?: CSSProperties | undefined
  children: ReactNode
}

/**
 * The root of a card.
 *
 * `data-ready` is the renderer's cue: `"0"` until the faces have loaded and every picture
 * inside has either loaded or given up, `"1"` after — so the capture can never catch a
 * fallback font or a half-fetched poster. Pictures report through `Art`, which registers
 * itself on mount and signs off on `load`, or on `error` once its fallback has loaded in
 * turn; the count of those still pending is what the flag reads.
 *
 * `data-format` names which format this is, for the test that renders each and for the
 * renderer's own poll, which looks for the one element carrying it. `data-frame` names which
 * shape it is being drawn in, which is no longer a function of the letter: the week's cover
 * and the gig card take either, and the stylesheet keys its story overrides on this attribute
 * rather than on a second class, so a card cannot be in one shape and styled as the other.
 *
 * The insets follow the frame and not the format, which is what the story's bottom band is
 * for: a strip of words 200 px from the bottom edge of a story sits under Instagram's reply
 * box, where a photograph may sit and a title may not.
 */
export function Frame({ format, frame, className, style: given, children }: FrameProps) {
  const shape = frame ?? defaultFrame(format)
  const story = shape === 'story'
  const { width, height } = FRAMES[shape]
  const padding = story
    ? `${STORY_TOP}px ${POST_INSET}px ${STORY_BOTTOM}px`
    : `${POST_BAND}px ${POST_INSET}px`

  const [fonts, setFonts] = useState(false)
  const [pending, setPending] = useState<readonly string[]>([])

  useEffect(() => {
    let live = true
    fontsSettled().then(() => {
      if (live) setFonts(true)
    })
    return () => {
      live = false
    }
  }, [])

  const gate = useMemo<Gate>(
    () => ({
      start: id => setPending(held => (held.includes(id) ? held : [...held, id])),
      end: id => setPending(held => held.filter(other => other !== id)),
    }),
    [],
  )

  // What a child positioned against the frame's own edges has to leave clear, which the
  // padding above cannot tell it: `.gig`'s photograph and its strip are absolute, so they do
  // not see it. Zero on a post, where the photograph bleeds to the edge on purpose and the
  // words in the strip sit inside it; the story's two bands on a story, where Instagram draws
  // its progress bar over the top and its reply box over the bottom — a photograph under
  // either is fine and a title under the reply box is not.
  const style = {
    ...given,
    width,
    height,
    padding,
    '--frame-top': `${story ? STORY_TOP : 0}px`,
    '--frame-band': `${story ? STORY_BOTTOM : 0}px`,
  } as CSSProperties
  const drawn = fonts && pending.length === 0
  return (
    <Loading.Provider value={gate}>
      <div
        className={`${s.frame} ${className}`}
        data-format={format}
        data-frame={shape}
        data-ready={drawn ? '1' : '0'}
        style={style}
      >
        <Drawn.Provider value={drawn}>{children}</Drawn.Provider>
      </div>
    </Loading.Provider>
  )
}

interface ArtProps {
  /** The picture, or null for a thing that has none. */
  src: string | null
  /** What to draw instead — when there is no picture, and when the picture fails. */
  fallback: string
  className?: string | undefined
  style?: CSSProperties | undefined
}

/**
 * A picture the frame waits for.
 *
 * A dead URL swaps to the fallback and stays pending until *that* has loaded: an `error` is
 * not "done", it is "try the plate", and the flag only clears when there is something on
 * the card to look at. A fallback that itself fails is the end of the line, and signs off,
 * or a broken plate would hold the renderer to its timeout for a picture nothing can fix.
 */
export function Art({ src, fallback, className, style }: ArtProps) {
  const id = useId()
  const gate = useContext(Loading)
  const [shown, setShown] = useState(src ?? fallback)

  useEffect(() => {
    setShown(src ?? fallback)
  }, [src, fallback])

  // Registered in the layout phase, so the flag reads "pending" before the browser has had
  // a chance to paint — or to resolve the fonts, whose wait starts in the effect after this.
  useLayoutEffect(() => {
    gate.start(id)
    return () => gate.end(id)
  }, [gate, id])

  return (
    <img
      src={shown}
      alt=""
      decoding="sync"
      className={className}
      style={style}
      onLoad={() => gate.end(id)}
      onError={() => (shown === fallback ? gate.end(id) : setShown(fallback))}
    />
  )
}

/** A gig's picture, or the plate it wears everywhere on the site when it has none. */
export function GigArt({
  id,
  src,
  className,
}: {
  id: string
  src: string | null
  className?: string | undefined
}) {
  return <Art src={src} fallback={bubbleFor(id)} className={className} />
}

/**
 * The wordmark, which is `/logo` — the chrome render `assets/` holds, downscaled — and never
 * the masthead's 320 px copy, which would be upscaled into a 240 px slot on a 1080 px frame.
 * The width is inline for the same reason the insets are: read off the element, per format.
 */
export function Wordmark({
  width = WORDMARK,
  className,
}: {
  width?: number
  className?: string | undefined
}) {
  return <Art src="/logo" fallback="/logo" className={`${s.wm} ${className ?? ''}`} style={{ width }} />
}

/**
 * The card's last line: where to go. `host` is the payload's, never a constant, so a card
 * the dev box draws names the dev box. A path that would make the line longer than
 * `LINK_LENGTH` is dropped rather than wrapped — the caption carries the exact link.
 */
export function link(host: string, path = ''): string {
  const full = `${host}${path}`
  return full.length > LINK_LENGTH ? host : full
}
